import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronRight, Plus, Search, Users } from 'lucide-react'
import { listContacts, createContact, type Contact } from '@/api/people'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { EmptyState } from '@/components/ui/empty-state'
import { PeopleSummary } from '@/components/people/PeopleSummary'
import { PersonRow } from '@/components/people/PersonRow'
import { PEOPLE_TABLE_COLUMNS } from '@/components/people/peopleUi'
import { groupPeople, totalsOf, type PeopleSort } from '@/components/people/peopleStats'
import { useIsReadOnly } from '@/hooks/useIsReadOnly'
import { DESKTOP_QUERY, useMediaQuery } from '@/hooks/useMediaQuery'
import { ROUTES } from '@/routes/routes'
import { cn } from '@/lib/utils'

const SEARCH_FROM = 6
const SECTION_LABEL =
  'mb-2 ml-1 text-xs font-semibold uppercase tracking-wider text-[var(--fl-text-2)]'

export default function PeoplePage() {
  const wide = useMediaQuery(DESKTOP_QUERY)
  const isReadOnly = useIsReadOnly()
  const [adding, setAdding] = useState(false)
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<PeopleSort>('biggest')
  const [showArchived, setShowArchived] = useState(false)
  const { data: contacts = [], isLoading } = useQuery({
    queryKey: ['contacts', { includeArchived: true }],
    queryFn: () => listContacts(true),
  })

  const activeCount = contacts.filter((c) => !c.archived).length
  const searchable = activeCount >= SEARCH_FROM
  const groups = groupPeople(contacts, searchable ? search : '', sort)
  const nothingMatches = groups.open.length + groups.settled.length + groups.archived.length === 0

  return (
    <div className="flex flex-col gap-3 p-4 sm:p-6 md:gap-5 md:p-10">
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-serif text-xl font-semibold text-[var(--fl-heading)] sm:text-3xl">
          People
        </h1>
        {!isReadOnly && contacts.length > 0 && (
          // The floating ＋ adds a transaction, so on a phone this stays a quiet pill.
          <Button
            aria-label="Add person"
            variant={wide ? 'default' : 'outline'}
            onClick={() => setAdding(true)}
            className={
              wide
                ? 'gap-2'
                : 'h-9 gap-1 rounded-full border-[var(--fl-primary)] px-3 text-[13px] font-semibold text-[var(--fl-primary)]'
            }
          >
            <Plus size={wide ? 16 : 14} /> {wide ? 'Add person' : 'Person'}
          </Button>
        )}
      </div>

      {adding && <AddPersonForm onDone={() => setAdding(false)} />}

      {!isLoading && contacts.length === 0 ? (
        !adding && (
          <EmptyState
            icon={Users}
            title="Keep track of who owes whom"
            description="Add friends and family you split bills with, pay for, or lend money to. Their balances count in your net worth."
            action={
              isReadOnly ? undefined : { label: 'Add person', onClick: () => setAdding(true) }
            }
          />
        )
      ) : (
        <>
          {activeCount > 0 && <PeopleSummary totals={totalsOf(contacts)} />}

          {searchable && (
            <div className="flex gap-2">
              <label className="flex h-11 flex-1 items-center gap-2 rounded-xl border border-[var(--fl-border)] bg-[var(--fl-surface)] px-3 text-[var(--fl-text-3)] sm:max-w-xs">
                <Search size={16} aria-hidden="true" />
                <input
                  type="search"
                  aria-label="Search people"
                  placeholder="Search people"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="min-w-0 flex-1 bg-transparent text-sm text-[var(--fl-text-1)] outline-none placeholder:text-[var(--fl-text-3)]"
                />
              </label>
              <Select
                aria-label="Sort people"
                value={sort}
                onChange={(v) => setSort(v as PeopleSort)}
                className="w-auto"
              >
                <option value="biggest">Biggest first</option>
                <option value="recent">Recent activity</option>
                <option value="name">A–Z</option>
              </Select>
            </div>
          )}

          {searchable && nothingMatches && (
            <p className="px-1 text-sm text-[var(--fl-text-2)]">
              No one called &ldquo;{search.trim()}&rdquo;.
            </p>
          )}

          {groups.open.length > 0 && (
            <PeopleGroup label={`Open · ${groups.open.length}`} people={groups.open} header />
          )}
          {groups.settled.length > 0 && (
            <PeopleGroup label={`All settled · ${groups.settled.length}`} people={groups.settled} />
          )}

          {groups.archived.length > 0 && (
            <div>
              <button
                type="button"
                aria-expanded={showArchived}
                onClick={() => setShowArchived((s) => !s)}
                className="flex h-11 items-center gap-1.5 px-1 text-[13px] text-[var(--fl-text-2)] hover:text-[var(--fl-heading)]"
              >
                <ChevronRight
                  size={14}
                  aria-hidden="true"
                  className={cn('transition-transform', showArchived && 'rotate-90')}
                />
                Archived · {groups.archived.length}
              </button>
              {showArchived && <PeopleGroup label="Archived" people={groups.archived} hideLabel />}
            </div>
          )}
        </>
      )}

      <p className="px-1 text-xs leading-relaxed text-[var(--fl-text-2)]">
        Don&rsquo;t remember who owes you? Add an &ldquo;Other receivables&rdquo; account in{' '}
        <Link to={ROUTES.APP.ACCOUNTS} className="font-medium underline">
          Accounts
        </Link>{' '}
        instead.
      </p>
    </div>
  )
}

function PeopleGroup({
  label,
  people,
  header = false,
  hideLabel = false,
}: {
  label: string
  people: Contact[]
  /** Column headings for the md+ table. */
  header?: boolean
  hideLabel?: boolean
}) {
  return (
    <section aria-label={label}>
      {!hideLabel && <h2 className={SECTION_LABEL}>{label}</h2>}
      <div className="overflow-hidden rounded-2xl border border-[var(--fl-border)] bg-[var(--fl-surface)] shadow-[var(--card-shadow-sm)]">
        {header && (
          <div
            aria-hidden="true"
            className={cn(
              'hidden border-b border-[var(--fl-divider)] px-5 py-2.5 text-xs font-semibold uppercase tracking-wide text-[var(--fl-text-2)]',
              PEOPLE_TABLE_COLUMNS
            )}
          >
            <span>Person</span>
            <span>Last activity</span>
            <span className="text-right">Owed to you</span>
            <span className="text-right">You owe</span>
            <span className="text-right">Net</span>
          </div>
        )}
        <ul>
          {people.map((c) => (
            <PersonRow key={c.id} contact={c} />
          ))}
        </ul>
      </div>
    </section>
  )
}

function AddPersonForm({ onDone }: { onDone: () => void }) {
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const mutation = useMutation({
    mutationFn: () => createContact({ name: name.trim() }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
      onDone()
    },
  })
  return (
    <form
      className="flex items-end gap-2 rounded-2xl border border-[var(--fl-border)] bg-[var(--fl-surface)] p-4"
      onSubmit={(e) => {
        e.preventDefault()
        if (name.trim()) mutation.mutate()
      }}
    >
      <div className="flex-1">
        <label
          htmlFor="person-name"
          className="mb-1 block text-sm font-medium text-[var(--fl-text-1)]"
        >
          Name
        </label>
        <Input
          id="person-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={100}
          autoFocus
        />
        {mutation.isError && (
          <p className="mt-1 text-xs text-[var(--fl-error)]">
            {(mutation.error as { response?: { data?: { detail?: string } } }).response?.data
              ?.detail ?? 'Could not add this person'}
          </p>
        )}
      </div>
      <Button type="submit" disabled={mutation.isPending}>
        Save
      </Button>
      <Button type="button" variant="ghost" onClick={onDone}>
        Cancel
      </Button>
    </form>
  )
}
